import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';

const LIBS = ['places'];
const MAP_CENTER_DEFAULT = { lat: 3.5209, lng: -76.2977 }; // Palmira, Valle del Cauca

const containerStyle = {
    width: '100%',
    height: '300px',
    borderRadius: '8px',
    overflow: 'hidden'
};

const MapaPicker = ({ apiKey, latitud, longitud, onChange, readOnly = false }) => {
    const [marker, setMarker] = useState(
        latitud && longitud
            ? { lat: parseFloat(latitud), lng: parseFloat(longitud) }
            : null
    );
    const [searchBox, setSearchBox] = useState(null);
    const mapRef = useRef(null);

    const center = marker || MAP_CENTER_DEFAULT;

    const onMapClick = useCallback((e) => {
        if (readOnly) return;
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setMarker(pos);
        onChange && onChange(pos.lat, pos.lng);
    }, [readOnly, onChange]);

    const onMarkerDrag = useCallback((e) => {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setMarker(pos);
        onChange && onChange(pos.lat, pos.lng);
    }, [onChange]);

    const onSearchLoad = useCallback((ref) => setSearchBox(ref), []);

    const onPlacesChanged = useCallback(() => {
        if (!searchBox) return;
        const places = searchBox.getPlaces();
        if (!places || places.length === 0) return;
        const place = places[0];
        if (!place.geometry) return;
        const pos = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
        };
        setMarker(pos);
        onChange && onChange(pos.lat, pos.lng);
        if (mapRef.current) {
            mapRef.current.panTo(pos);
            mapRef.current.setZoom(17);
        }
    }, [searchBox, onChange]);

    return (
        <LoadScript googleMapsApiKey={apiKey} libraries={LIBS}>
            <div>
                {!readOnly && (
                    <StandaloneSearchBox onLoad={onSearchLoad} onPlacesChanged={onPlacesChanged}>
                        <input
                            type="text"
                            placeholder="Buscar dirección en el mapa..."
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                marginBottom: '8px',
                                boxSizing: 'border-box',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px'
                            }}
                        />
                    </StandaloneSearchBox>
                )}

                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={marker ? 17 : 13}
                    onClick={onMapClick}
                    onLoad={map => mapRef.current = map}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: true,
                        zoomControl: true,
                    }}
                >
                    {marker && (
                        <Marker
                            position={marker}
                            draggable={!readOnly}
                            onDragEnd={onMarkerDrag}
                        />
                    )}
                </GoogleMap>

                {marker && (
                    <div style={{
                        display: 'flex', gap: '8px', marginTop: '8px',
                        padding: '8px 10px',
                        background: 'var(--bg-success, #f0fdf4)',
                        border: '0.5px solid var(--border-success, #86efac)',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: 'var(--text-success, #166534)'
                    }}>
                        <span>📍</span>
                        <span>Lat: <strong>{marker.lat.toFixed(7)}</strong></span>
                        <span>Lng: <strong>{marker.lng.toFixed(7)}</strong></span>
                        {!readOnly && (
                            <button
                                onClick={() => { setMarker(null); onChange && onChange(null, null); }}
                                style={{
                                    marginLeft: 'auto', background: 'none',
                                    border: 'none', cursor: 'pointer',
                                    fontSize: '11px', color: 'var(--text-muted, #6b7280)'
                                }}
                            >
                                ✕ Quitar pin
                            </button>
                        )}
                    </div>
                )}

                {!marker && !readOnly && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)', marginTop: '6px' }}>
                        Toca el mapa o busca una dirección para colocar el pin
                    </p>
                )}
            </div>
        </LoadScript>
    );
};

export default MapaPicker;
